import React, { use, useEffect, useMemo } from 'react';
import { HeaderThElement } from '@progress/kendo-react-data-tools';
import { GridCellProps, GridCustomHeaderCellProps } from '@progress/kendo-react-grid';
import Card from '@components-clean/core/card';
import ErrorMessage from '@components-clean/core/error';
import Grid from '@components-clean/core/grid-fe';
import { MultiValueDataCell, PanelWeightCell, RagValueCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import { SiteHubGridData } from '@interfaces/central-monitoring';
import { StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { useLocalState, useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import SiteHubApi from '@apis/bff-api/central-monitoring/site-hub';

const GridView = () => {
    const { t }: { t: (key: string) => string } = useTranslation('cm', {
        keyPrefix: 'siteHub.siteGrid',
    });

    const { state: localState } = useLocalState();
    const studyName = localState?.selectedStudy?.studyNumber;

    const { current } = use(StudySiteHubContext);

    const { studyRef } = current;
    const datasetIdInFocus = current.dataset?.id;
    const datasetDateInFocus = stringDateWithoutTimeFromUtc(current.dataset?.date, 'DD-MMM-YYYY')!;
    const { siteId, siteName } = current;
    const comparisonDatasetId = current.datasetFilters.comparisonDataset?.id ?? undefined;
    const comparisonDatasetDate = stringDateWithoutTimeFromUtc(
        current.datasetFilters.comparisonDataset?.dataCutDate ?? null,
        'DD-MMM-YYYY',
    )!;

    const {
        data,
        error,
        isMutating: isLoading,
        trigger,
    } = SiteHubApi.useGetSiteGridData(studyRef, datasetIdInFocus, siteId, comparisonDatasetId);

    useEffect(() => {
        const triggerRequest = () => {
            if (!siteId) {
                return;
            }
            trigger().catch((err) => window.console.log(err));
        };
        triggerRequest();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [siteId, studyRef, datasetIdInFocus, comparisonDatasetId]);

    interface ITableInfo {
        columns: GridColumnProps[];
        data: { [key: string]: unknown }[];
    }

    const getTableColumn = (valueField: string, isNumeric: boolean, isRagColumn: boolean): GridColumnProps[] => {
        if (comparisonDatasetId !== undefined && isNumeric) {
            return [
                {
                    id: valueField,
                    title: t(`columns.${valueField}`),
                    lockVisibility: true,
                    locked: false,
                    sortable: false,
                    children: [
                        {
                            id: `${valueField}_values`,
                            field: valueField,
                            title: t('columns.values'),
                            customColumn: true,
                            width: 250,
                            minWidth: 200,
                            filterKey: 'valueInFocus',
                            comparisonFilterKey: 'comparisonValue',
                            cells: {
                                data: (props) =>
                                    MultiValueDataCell({ ...props, highlighted: isRagColumn, numberFormat: '0.00' }),
                            },
                            ragColumn: isRagColumn,
                            lockVisibility: true,
                            filter: 'numeric',
                            sortable: true,
                            multiValueTypeColumn: true,
                            multiValueExportTitles: isRagColumn
                                ? {
                                      comparisonValue: comparisonDatasetDate
                                          ? `${comparisonDatasetDate} ${t('exportColumns.value')}`
                                          : t('exportColumns.comparisonValue'),
                                      comparisonColour: comparisonDatasetDate
                                          ? `${comparisonDatasetDate} ${t('exportColumns.colour')}`
                                          : t('exportColumns.comparisonColour'),
                                      valueInFocus: datasetDateInFocus
                                          ? `${datasetDateInFocus} ${t('exportColumns.value')}`
                                          : t('exportColumns.valueInFocus'),
                                      colourInFocus: datasetDateInFocus
                                          ? `${datasetDateInFocus} ${t('exportColumns.colour')}`
                                          : t('exportColumns.colourInFocus'),
                                      changeDirection: t('exportColumns.changeDirection'),
                                  }
                                : {
                                      comparisonValue: comparisonDatasetDate
                                          ? `${comparisonDatasetDate} ${t('exportColumns.value')}`
                                          : t('exportColumns.comparisonValue'),
                                      valueInFocus: datasetDateInFocus
                                          ? `${datasetDateInFocus} ${t('exportColumns.value')}`
                                          : t('exportColumns.valueInFocus'),
                                      changeDirection: t('exportColumns.changeDirection'),
                                  },
                        },
                        {
                            id: `${valueField}_change`,
                            field: `${valueField}.change`,
                            title: t('columns.change'),
                            minWidth: 90,
                            customColumn: true,
                            lockVisibility: true,
                            format: '{0:0.00}',
                            filter: 'numeric',
                        },
                    ],
                },
            ];
        }
        return [
            {
                id: valueField,
                field: valueField,
                title: t(`columns.${valueField}`),
                minWidth: isNumeric ? 105 : 289,
                customColumn: true,
                format: isNumeric ? '{0:0.00}' : undefined,
                filter: isNumeric ? 'numeric' : undefined,
                ragColumn: isRagColumn,
                filterKey: isRagColumn ? 'rateOrScore' : undefined,
                cells: {
                    data: isRagColumn
                        ? (props: GridCellProps) => RagValueCell({ ...props, numberFormat: '0.00' })
                        : undefined,
                },
                multiValueExportTitles: isRagColumn
                    ? {
                          rateOrScore: t('exportColumns.value'),
                          colour: t('exportColumns.colour'),
                      }
                    : undefined,
            },
        ];
    };

    const tableInfo = useMemo(() => {
        if (!data) return undefined;

        const info: ITableInfo = {
            columns: [],
            data: [],
        };

        info.columns.push({
            id: 'panelWeightDetails',
            cells: {
                data: PanelWeightCell,
                headerCell: (props: GridCustomHeaderCellProps) => {
                    return (
                        <HeaderThElement
                            columnId={props.thProps?.columnId || ''}
                            {...props.thProps}
                            data-multi-column={false}
                        >
                            <span style={{ visibility: 'hidden' }}>{props.children}</span>
                        </HeaderThElement>
                    );
                },
            },
            // This prop needs to be defined for the Excel export.
            title: 'Weight if Included in Scoring',
            // This prop need to defined for sorting to work correctly.
            field: 'panelWeightDetails.weight',
            customColumn: true,
            width: 50,
            filterable: false,
            resizable: false,
            locked: true,
        });

        info.columns.push(...getTableColumn('plotTitle', false, false));
        info.columns.push({
            id: 'yAxisTitle',
            field: 'yAxisTitle',
            title: t('columns.yAxisTitle'),
            sortable: false,
            filterable: false,
            children: [
                ...getTableColumn('yAxisLabel', false, false),
                ...(comparisonDatasetId !== undefined
                    ? getTableColumn('yAxisValue', true, false)[0].children!
                    : getTableColumn('yAxisValue', true, false)),
            ],
        });
        info.columns.push({
            id: 'yVolumeTitle',
            field: 'yVolumeTitle',
            title: t('columns.yVolumeTitle'),
            sortable: false,
            filterable: false,
            children: [
                ...getTableColumn('yVolumeLabel', false, false),
                ...(comparisonDatasetId !== undefined
                    ? getTableColumn('yVolumeValue', true, false)[0].children!
                    : getTableColumn('yVolumeValue', true, false)),
            ],
        });
        info.columns.push({
            id: 'xAxisTitle',
            field: 'xAxisTitle',
            title: t('columns.xAxisTitle'),
            sortable: false,
            filterable: false,
            children: [
                ...getTableColumn('xAxisLabel', false, false),
                ...(comparisonDatasetId !== undefined
                    ? getTableColumn('xAxisValue', true, false)[0].children!
                    : getTableColumn('xAxisValue', true, false)),
            ],
        });
        info.columns.push(...getTableColumn('score', true, true));

        data.forEach((siteHubGridData: SiteHubGridData) => {
            const flattenedRow: { [key: string]: unknown } = {
                reference: siteHubGridData.reference,
                plotTitle: siteHubGridData.plotTitle,
                panelWeightDetails: {
                    includeInSiteScoring: siteHubGridData.includeInSiteScoring as boolean | null | undefined,
                    weight: (siteHubGridData.includeInSiteScoring ? siteHubGridData.weight : undefined) as
                        boolean | null | undefined,
                },
                yAxisLabel: siteHubGridData.yAxisLabel,
                yVolumeLabel: siteHubGridData.yVolumeLabel,
                xAxisLabel: siteHubGridData.xAxisLabel,
                yAxisValue:
                    comparisonDatasetId !== undefined
                        ? {
                              comparisonValue: siteHubGridData.studyHubSiteGridPanelData?.yAxisPlot?.comparisonValue,
                              valueInFocus: siteHubGridData.studyHubSiteGridPanelData?.yAxisPlot?.inFocusValue,
                              change: siteHubGridData.studyHubSiteGridPanelData?.yAxisPlot?.change,
                              changeDirection: siteHubGridData.studyHubSiteGridPanelData?.yAxisPlot?.direction,
                          }
                        : siteHubGridData.studyHubSiteGridPanelData?.yAxisPlot?.inFocusValue,
                yVolumeValue:
                    comparisonDatasetId !== undefined
                        ? {
                              comparisonValue: siteHubGridData.studyHubSiteGridPanelData?.yAxisCount?.comparisonValue,
                              valueInFocus: siteHubGridData.studyHubSiteGridPanelData?.yAxisCount?.inFocusValue,
                              change: siteHubGridData.studyHubSiteGridPanelData?.yAxisCount?.change,
                              changeDirection: siteHubGridData.studyHubSiteGridPanelData?.yAxisCount?.direction,
                          }
                        : siteHubGridData.studyHubSiteGridPanelData?.yAxisCount?.inFocusValue,
                xAxisValue:
                    comparisonDatasetId !== undefined
                        ? {
                              comparisonValue: siteHubGridData.studyHubSiteGridPanelData?.xAxisCount?.comparisonValue,
                              valueInFocus: siteHubGridData.studyHubSiteGridPanelData?.xAxisCount?.inFocusValue,
                              change: siteHubGridData.studyHubSiteGridPanelData?.xAxisCount?.change,
                              changeDirection: siteHubGridData.studyHubSiteGridPanelData?.xAxisCount?.direction,
                          }
                        : siteHubGridData.studyHubSiteGridPanelData?.xAxisCount?.inFocusValue,
                score:
                    comparisonDatasetId !== undefined
                        ? {
                              comparisonValue: siteHubGridData.studyHubSiteGridPanelData?.score?.comparisonValue,
                              valueInFocus: siteHubGridData.studyHubSiteGridPanelData?.score?.inFocusValue,
                              change: siteHubGridData.studyHubSiteGridPanelData?.score?.change,
                              changeDirection: siteHubGridData.studyHubSiteGridPanelData?.score?.direction,
                              comparisonColour: siteHubGridData.studyHubSiteGridPanelData?.score?.comparisonColour,
                              colourInFocus: siteHubGridData.studyHubSiteGridPanelData?.score?.inFocusColour,
                          }
                        : {
                              rateOrScore: siteHubGridData.studyHubSiteGridPanelData?.score?.inFocusValue,
                              colour: siteHubGridData.studyHubSiteGridPanelData?.score?.inFocusColour,
                          },
            };
            info.data.push(flattenedRow);
        });

        return info;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    if (isLoading || error || !siteId) {
        return (
            <Card data-testid="site-grid-loading-card" title={t('title')}>
                {isLoading ? <Loading /> : <ErrorMessage />}
            </Card>
        );
    }

    const exportFileName = comparisonDatasetDate
        ? `Panel Scores_${studyName}_${siteName}_Dataset(s) ${comparisonDatasetDate} - ${datasetDateInFocus}`
        : `Panel Scores_${studyName}_${siteName}_Dataset(s) ${datasetDateInFocus}`;

    return (
        <Grid
            title={t('title')}
            maxHeight="auto"
            testDataId="site-grid"
            data={tableInfo?.data ?? []}
            columns={tableInfo?.columns ?? []}
            loading={isLoading}
            useLoadingOverlay
            exportFilename={exportFileName}
            showAddIcon={false}
            service={AuditServiceType.CENTRAL_MONITORING_2}
            area={AuditApplicationArea.CENTRAL_MONITORING_2}
            dataType={AuditDataType.EXCEL_EXPORT} // The data doesn't have an OPRA type so just use this
            auditDescriptionPrefix="CM - Site Hub"
        />
    );
};

export default GridView;
